import { useCallback, useEffect, useRef, useState } from "react";
import {
  cancelPayrollRunPayment,
  cancelPayrollRunRemittance,
  createPayrollRun,
  discardPayrollRun,
  listPayrollRun,
  listPayrollRunItems,
  payPayrollRun,
  postPayrollRun,
  remitPayrollRun,
  voidPayrollRun,
} from "./actions";
import { sortPayrollItems } from "../../Utils/payroll";

const EMPTY = { run: null, items: [], loading: false, error: null };

/**
 * usePayrollRun - one business's payroll run for one month.
 *
 * Reads through AppSync; `create` and `discard` go through the payroll Lambda
 * and put its answer straight into state. The Lambda already returns the whole
 * run, so there is no read-after-write against a GSI that may not have caught
 * up yet.
 *
 * Answers that arrive after the month changed are dropped: switching from
 * September to October while September is still loading (or computing) must
 * never paint September's figures under October's heading.
 *
 * Posting, paying, cancelling a payment and voiding each answer with the run
 * as it now stands. Its items mirror the run's status, so they follow it here
 * too; a voided run leaves the month empty, ready to be computed again.
 */
export const usePayrollRun = (businessID, period) => {
  const [state, setState] = useState({ ...EMPTY, loading: Boolean(businessID && period) });
  const [creating, setCreating] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  // "post" | "pay" | "cancelPayment" | "void" while one is in flight.
  const [pending, setPending] = useState(null);
  const [actionError, setActionError] = useState(null);

  const key = `${businessID}|${period}`;
  const currentKey = useRef(key);
  currentKey.current = key;
  const readSeq = useRef(0);

  const refetch = useCallback(async () => {
    if (!businessID || !period) {
      setState(EMPTY);
      return;
    }
    const seq = ++readSeq.current;
    const loadKey = `${businessID}|${period}`;
    // A refetch of the same month keeps its run on screen. A new month drops
    // the old one at once: left in place, Post, Void and Pay stayed live and
    // acted on last month's run under this month's heading.
    setState((prev) =>
      prev.key === loadKey
        ? { ...prev, loading: true, error: null }
        : { ...EMPTY, loading: true, key: loadKey },
    );
    try {
      const run = await listPayrollRun({ businessID, period });
      const items = run ? await listPayrollRunItems({ runID: run.id }) : [];
      if (seq !== readSeq.current) return;
      setState({
        run,
        items: sortPayrollItems(items),
        loading: false,
        error: null,
        key: loadKey,
      });
    } catch (error) {
      if (seq !== readSeq.current) return;
      setState({ ...EMPTY, error, key: loadKey });
    }
  }, [businessID, period]);

  useEffect(() => {
    setActionError(null);
    refetch();
  }, [refetch]);

  const create = useCallback(async () => {
    const requestedKey = key;
    setCreating(true);
    setActionError(null);
    try {
      const data = await createPayrollRun({ businessID, period });
      if (currentKey.current !== requestedKey) return null;
      // Supersedes any read still in flight for this month.
      readSeq.current += 1;
      setState({
        run: data.run,
        items: sortPayrollItems(data.items),
        loading: false,
        error: null,
        key: requestedKey,
      });
      return data;
    } catch (error) {
      if (currentKey.current === requestedKey) setActionError(error);
      return null;
    } finally {
      setCreating(false);
    }
  }, [businessID, period, key]);

  const discard = useCallback(async () => {
    const run = state.run;
    if (!run) return false;
    const requestedKey = key;
    setDiscarding(true);
    setActionError(null);
    try {
      await discardPayrollRun({ id: run.id });
      if (currentKey.current !== requestedKey) return false;
      readSeq.current += 1;
      setState({ ...EMPTY, key: requestedKey });
      return true;
    } catch (error) {
      if (currentKey.current === requestedKey) setActionError(error);
      return false;
    } finally {
      setDiscarding(false);
    }
  }, [state.run, key]);

  const runAction = useCallback(
    async (kind, request) => {
      const run = state.run;
      if (!run) return null;
      const requestedKey = key;
      setPending(kind);
      setActionError(null);
      try {
        const data = await request(run);
        if (currentKey.current !== requestedKey) return null;
        readSeq.current += 1;
        if (data.run.status === "VOID") {
          setState({ ...EMPTY, key: requestedKey });
        } else {
          setState((prev) => ({
            ...prev,
            run: data.run,
            items: prev.items.map((item) => ({ ...item, status: data.run.status })),
          }));
        }
        return data;
      } catch (error) {
        if (currentKey.current === requestedKey) setActionError(error);
        return null;
      } finally {
        setPending(null);
      }
    },
    [state.run, key],
  );

  const post = useCallback(
    () => runAction("post", (run) => postPayrollRun({ id: run.id, revision: run.revision })),
    [runAction],
  );
  const pay = useCallback(
    ({ accountID, paidAt }) =>
      runAction("pay", (run) => payPayrollRun({ id: run.id, accountID, paidAt })),
    [runAction],
  );
  const cancelPayment = useCallback(
    () => runAction("cancelPayment", (run) => cancelPayrollRunPayment({ id: run.id })),
    [runAction],
  );
  const voidRun = useCallback(
    () => runAction("void", (run) => voidPayrollRun({ id: run.id })),
    [runAction],
  );
  const remit = useCallback(
    (kind, { accountID, paidAt, ntpn }) =>
      runAction(`remit:${kind}`, (run) =>
        remitPayrollRun({ id: run.id, kind, accountID, paidAt, ntpn }),
      ),
    [runAction],
  );
  const cancelRemittance = useCallback(
    (kind) =>
      runAction(`cancelRemittance:${kind}`, (run) =>
        cancelPayrollRunRemittance({ id: run.id, kind }),
      ),
    [runAction],
  );

  const clearActionError = useCallback(() => setActionError(null), []);

  return {
    ...state,
    refetch,
    create,
    creating,
    discard,
    discarding,
    post,
    posting: pending === "post",
    pay,
    paying: pending === "pay",
    cancelPayment,
    cancellingPayment: pending === "cancelPayment",
    voidRun,
    voiding: pending === "void",
    remit,
    cancelRemittance,
    // The kind being paid or cancelled, e.g. "PPH21", or null.
    remittingKind: pending?.startsWith("remit:") ? pending.slice(6) : null,
    cancellingRemittanceKind: pending?.startsWith("cancelRemittance:")
      ? pending.slice(17)
      : null,
    actionError,
    clearActionError,
  };
};
